/* Bootstrap Login — Elite / High-security / AWS-console minimalism.
 * Reserved for the platform owner (BOOTSTRAP_ADMIN_EMAIL). */
import { useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Lock, ArrowRight, ShieldAlert, KeyRound } from 'lucide-react';
import api from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';

function fmt(detail) {
  if (!detail) return 'Access denied.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map(e => e?.msg || String(e)).join(' ');
  return detail?.msg || String(detail);
}

export default function BootstrapLogin() {
  const nav = useNavigate();
  const { checkAuth } = useAuth();
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ email: '', password: '' });

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    try {
      await api.post('/auth/login', {
        email: form.email.trim().toLowerCase(),
        password: form.password,
        portal: 'bootstrap',
      });
      await checkAuth();
      nav('/bootstrap/dashboard', { replace: true });
    } catch (err) {
      toast.error(fmt(err?.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <div
      className="relative min-h-screen bg-[#050708] text-white overflow-hidden font-mono"
      data-testid="bootstrap-login-root"
      style={{
        backgroundImage: `
          radial-gradient(circle at 0% 0%, rgba(255, 149, 0, 0.06) 0%, transparent 40%),
          radial-gradient(circle at 100% 100%, rgba(255, 149, 0, 0.06) 0%, transparent 40%),
          linear-gradient(rgba(255,255,255,0.02) 1px, transparent 1px),
          linear-gradient(90deg, rgba(255,255,255,0.02) 1px, transparent 1px)
        `,
        backgroundSize: '100% 100%, 100% 100%, 48px 48px, 48px 48px',
      }}
    >
      {/* Corner labels */}
      <div className="absolute top-4 left-4 text-[10px] uppercase tracking-[0.3em] text-white/40">
        wildquest / bootstrap
      </div>
      <div className="absolute top-4 right-4 text-[10px] uppercase tracking-[0.3em] text-white/40">
        restricted
      </div>
      <div className="absolute bottom-4 left-4 text-[9px] uppercase tracking-widest text-white/25">
        v2.2.0 · platform-owner-only
      </div>

      <div className="relative z-10 min-h-screen flex items-center justify-center px-6">
        <motion.div
          initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="w-full max-w-md"
        >
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-2 px-3 py-1 border border-amber-500/40 bg-amber-500/5">
              <ShieldAlert className="w-3.5 h-3.5 text-amber-400" strokeWidth={1.6} />
              <span className="text-[10px] uppercase tracking-[0.35em] text-amber-300">Platform Owner Access</span>
            </div>
            <h1 className="mt-6 font-mono text-3xl tracking-tight text-white/95">
              WildQuest <span className="text-amber-400">Bootstrap</span> Console
            </h1>
            <p className="mt-3 text-[11px] text-white/50 leading-relaxed">
              Emergency recovery, system configuration, and superuser access.<br/>
              All activity is logged. Unauthorized attempts trigger alerts.
            </p>
          </div>

          <form onSubmit={submit} className="border border-white/10 bg-black/40 backdrop-blur p-6 space-y-4" data-testid="bootstrap-form">
            <label className="block">
              <span className="text-[10px] uppercase tracking-[0.25em] text-white/50 flex items-center gap-1.5 mb-2">
                <KeyRound className="w-3 h-3" /> Owner Email
              </span>
              <input
                data-testid="boot-input-email"
                type="email" autoComplete="username" required
                value={form.email}
                onChange={e => setForm({ ...form, email: e.target.value })}
                placeholder="platform-owner@domain"
                className="w-full px-3 py-2.5 bg-transparent border border-white/15 text-sm outline-none focus:border-amber-400/60"
              />
            </label>
            <label className="block">
              <span className="text-[10px] uppercase tracking-[0.25em] text-white/50 flex items-center gap-1.5 mb-2">
                <Lock className="w-3 h-3" /> Master Password
              </span>
              <input
                data-testid="boot-input-password"
                type="password" autoComplete="current-password" required
                value={form.password}
                onChange={e => setForm({ ...form, password: e.target.value })}
                placeholder="••••••••••"
                className="w-full px-3 py-2.5 bg-transparent border border-white/15 text-sm outline-none focus:border-amber-400/60"
              />
            </label>
            <button
              type="submit"
              disabled={busy}
              data-testid="boot-submit"
              className="w-full py-3 bg-amber-500 text-black text-[11px] uppercase tracking-[0.3em] font-semibold flex items-center justify-center gap-2 disabled:opacity-50 hover:bg-amber-400 transition-colors"
            >
              {busy ? 'AUTHENTICATING…' : 'AUTHENTICATE'}
              <ArrowRight className="w-3.5 h-3.5" />
            </button>
            <div className="flex items-center justify-between pt-1 text-[9px] uppercase tracking-widest text-white/30">
              <button type="button" onClick={() => nav('/')} data-testid="boot-player">← Player portal</button>
              <button type="button" onClick={() => nav('/admin/login')} data-testid="boot-admin">Admin portal →</button>
            </div>
          </form>

          <div className="mt-6 pl-3 border-l border-amber-500/30 text-[10px] text-white/40 leading-relaxed">
            <p className="uppercase tracking-widest text-amber-400/80 text-[9px] mb-1">Security Notice</p>
            Attempts against this endpoint are rate-limited to 5 failures per 15 minutes.
            All events are written to the immutable audit trail.
          </div>
        </motion.div>
      </div>
    </div>
  );
}
